'use client';

import dynamic from 'next/dynamic';
import { useLocale } from 'next-intl';
import { useTheme } from 'next-themes';
import * as React from 'react';
import { useResizeObserver } from 'usehooks-ts';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { DialogLogOut } from '@/lib/components/Account/Settings/DialogLogOut';
import { SIGNUP_PATH } from '@/lib/components/Header/routes';
import { HeaderV2, type HeaderV2User } from '@/lib/components/HeaderV2';
import { LEADERBOARD_PATH, PROMOCODES_HREF } from '@/lib/components/RewardsHub/routes';
import { FEATURE_FLAGS } from '@/lib/config/features';
import { DOCS_URL } from '@/lib/constants/docs';
import { useLanguageSelection } from '@/lib/hooks/useLanguageSelection';
import { useHeaderBalanceCurrency } from '@/lib/providers/HeaderBalanceCurrencyContext';
import { useLayoutDimensionsStore } from '@/lib/providers/LayoutDimensionsStoreProvider';
import { usePathname, useRouter } from '@/lib/routing';
import { useWalletDialogsStore } from '@/lib/store/wallet-dialogs-store';
import { WORDMARK_ON_DARK, WORDMARK_ON_LIGHT } from '@/lib/ui-kit/animated-logo';
import { getZoneFromPath } from '@/lib/utils/zones';

const DialogDeposit = dynamic(
  () => import('./Account/Wallet/DialogDeposit').then((mod) => mod.DialogDeposit),
  { ssr: false },
);

const DialogConvert = dynamic(
  () => import('./Account/Wallet/DialogConverter').then((mod) => mod.DialogConvert),
  { ssr: false },
);

const DialogAddEmail = dynamic(
  () => import('./Account/Settings/DialogAddEmail').then((mod) => mod.DialogAddEmail),
  { ssr: false },
);

interface HeaderV2ContainerProps {
  user?: HeaderV2User | null;
}

export function HeaderV2Container({ user: userOverride }: HeaderV2ContainerProps = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();

  // Returns true when the current URL already matches the given internal href
  // (path + every query param in `target`). Used to no-op nav clicks that
  // would otherwise spin a progress bar over an unchanged route.
  // Reads location rather than useSearchParams on purpose: that hook makes
  // React client-render the whole header on every prerendered route (see
  // nextjs.org/docs/app/api-reference/functions/use-search-params#behavior),
  // so the header was missing from the initial HTML entirely. It only runs
  // from click handlers, where location is always current.
  const isCurrentRoute = React.useCallback(
    (target: string) => {
      const [path, query] = target.split('?');
      if (pathname !== path) return false;
      if (!query) return true;
      const current = new URLSearchParams(window.location.search);
      for (const [k, v] of new URLSearchParams(query).entries()) {
        if (current.get(k) !== v) return false;
      }
      return true;
    },
    [pathname],
  );
  const { user: authUser, isAuthenticated, isLoading: isAuthLoading } = useAuthContext();

  // Which currency the wallet popover's trading balance shows. The trade zone
  // overrides this with the selected pair's settle coin (USDT / USDC / base
  // coin for inverse); everywhere else it stays USDC.
  const displaySymbol = (useHeaderBalanceCurrency() ?? 'USDC').toUpperCase();

  const user: HeaderV2User | null | undefined = React.useMemo(() => {
    if (userOverride !== undefined) return userOverride;
    if (!isAuthenticated || !authUser) return null;
    return {
      id: authUser.id,
      name: authUser.name ?? authUser.email,
      email: authUser.email,
    };
  }, [userOverride, authUser, isAuthenticated]);

  const { resolvedTheme: chosenTheme, forcedTheme, setTheme } = useTheme();
  // A page pinned to one theme (the Rewards Hub) wins over the player's pick —
  // resolvedTheme alone would still report the pick.
  const resolvedTheme = forcedTheme ?? chosenTheme;
  // Single hydration flag — guards both theme (resolvedTheme is undefined on
  // server) and auth-driven action UI (server always renders Skeleton until
  // we know the auth state from localStorage on the client).
  const [isHydrated, setIsHydrated] = React.useState(false);
  React.useEffect(() => {
    setIsHydrated(true);
  }, []);
  const isThemeMounted = isHydrated;
  const [isLogOutDialogOpen, setIsLogOutDialogOpen] = React.useState(false);

  const handleLogout = React.useCallback(() => {
    setIsLogOutDialogOpen(true);
  }, []);

  // Global store, not local state — other zones (e.g. the trade margin-call
  // toast) open these dialogs in place instead of routing to /account.
  const isDepositDialogOpen = useWalletDialogsStore((s) => s.depositOpen);
  const isConvertDialogOpen = useWalletDialogsStore((s) => s.convertOpen);
  const setIsDepositDialogOpen = useWalletDialogsStore((s) => s.setDepositOpen);
  const setIsConvertDialogOpen = useWalletDialogsStore((s) => s.setConvertOpen);

  const handleDeposit = React.useCallback(() => {
    setIsDepositDialogOpen(true);
  }, [setIsDepositDialogOpen]);

  const handleConvert = React.useCallback(() => {
    setIsConvertDialogOpen(true);
  }, [setIsConvertDialogOpen]);

  const [isAddEmailDialogOpen, setIsAddEmailDialogOpen] = React.useState(false);
  // Remount the dialog on each open (via key bump) so it always starts on the
  // form step — mirrors SettingsContent's add-email handling.
  const [addEmailKey, setAddEmailKey] = React.useState(0);

  const handleAddEmail = React.useCallback(() => {
    setAddEmailKey((n) => n + 1);
    setIsAddEmailDialogOpen(true);
  }, []);

  const handleProfile = React.useCallback(() => {
    if (!user?.id) return;
    const target = `/stats/profile/${user.id}`;
    if (isCurrentRoute(target)) return;
    router.push(target);
  }, [router, isCurrentRoute, user?.id]);

  const handleWallet = React.useCallback(() => {
    if (isCurrentRoute('/account?tab=wallet')) return;
    router.push('/account?tab=wallet');
  }, [router, isCurrentRoute]);

  const handlePromocodes = React.useCallback(() => {
    if (isCurrentRoute(PROMOCODES_HREF)) return;
    router.push(PROMOCODES_HREF);
  }, [router, isCurrentRoute]);

  const handleSettings = React.useCallback(() => {
    if (isCurrentRoute('/account?tab=settings')) return;
    router.push('/account?tab=settings');
  }, [router, isCurrentRoute]);

  // The menu's Leaderboard and News rows can cross zones: the leaderboard is
  // apps/web, News is apps/news. The soft router only knows the current zone's pages, so a
  // cross-zone target is a full page load, same as a plain <a>.
  const zone = getZoneFromPath(pathname);

  // The leaderboard took over from the Stats page (19.10).
  const handleLeaderboard = React.useCallback(() => {
    if (zone !== 'web') {
      window.location.assign(`/${locale}${LEADERBOARD_PATH}`);
      return;
    }
    if (isCurrentRoute(LEADERBOARD_PATH)) return;
    router.push(LEADERBOARD_PATH);
  }, [zone, locale, router, isCurrentRoute]);

  const handleNews = React.useCallback(() => {
    if (zone !== 'news') {
      window.location.assign(`/${locale}/news`);
      return;
    }
    if (isCurrentRoute('/news')) return;
    router.push('/news');
  }, [zone, locale, router, isCurrentRoute]);

  // Same zone as the leaderboard: apps/web.
  const handleRewardsHub = React.useCallback(() => {
    if (zone !== 'web') {
      window.location.assign(`/${locale}/rewards`);
      return;
    }
    if (isCurrentRoute('/rewards')) return;
    router.push('/rewards');
  }, [zone, locale, router, isCurrentRoute]);

  const handleApi = React.useCallback(() => {
    window.open(DOCS_URL.apiIntroduction, '_blank', 'noopener,noreferrer');
  }, []);

  const handleHelpCenter = React.useCallback(() => {
    if (typeof window !== 'undefined') window.open(DOCS_URL.home, '_blank');
  }, []);

  const { selectedLocale, languageOptions, handleLanguageChange } = useLanguageSelection({
    redirectOnChange: true,
    showToast: false,
  });

  const headerRef = React.useRef<HTMLDivElement>(null);
  const { width: headerWidth, height: headerHeight } = useResizeObserver({
    ref: headerRef as React.RefObject<HTMLDivElement>,
    box: 'border-box',
  });
  const { setNavbarDimensions } = useLayoutDimensionsStore();
  React.useEffect(() => {
    setNavbarDimensions({ width: headerWidth, height: headerHeight });
  }, [headerWidth, headerHeight, setNavbarDimensions]);

  return (
    <>
      {/* data-app-header: panels that open as a page under the header
          measure where it ends (useAppHeaderBottom). */}
      <div ref={headerRef} data-app-header className="sticky top-0 z-30">
        <HeaderV2
          // The divider is always drawn. It used to fade in past 4px of scroll,
          // which meant the header had no bottom edge at rest and the line
          // appeared out of nowhere on the first flick of the wheel.
          // transition-colors stays for the theme switch, not for the border.
          // Frosted fill lives on ::before — backdrop-filter on the header itself
          // would trap any position:fixed descendant inside the header box.
          className="relative border-b border-solid border-v2-border transition-colors duration-300 before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-v2-surface-primary/80 before:backdrop-blur-[16px] before:transition-colors before:duration-300"
          user={user}
          isAuthLoading={userOverride === undefined && (!isHydrated || isAuthLoading)}
          // Same hydration guard the logoSrc swap used: resolvedTheme is
          // undefined on the server, so until hydration we must render the
          // dark-theme wordmark or it turns black-on-black for a frame.
          wordmarkFill={
            isThemeMounted && resolvedTheme === 'light' ? WORDMARK_ON_LIGHT : WORDMARK_ON_DARK
          }
          onLogin={() => router.push('/auth/login')}
          onSignup={() => router.push(SIGNUP_PATH)}
          onDeposit={handleDeposit}
          balanceCurrency={displaySymbol}
          onConvert={handleConvert}
          theme={resolvedTheme === 'dark' ? 'dark' : 'light'}
          onThemeChange={(t) => setTheme(t)}
          onProfileClick={handleProfile}
          onWalletClick={handleWallet}
          onRewardsHubClick={handleRewardsHub}
          onPromocodesClick={FEATURE_FLAGS.ENABLE_PROMOCODES ? handlePromocodes : undefined}
          onLeaderboardClick={handleLeaderboard}
          onNewsClick={handleNews}
          onApiClick={handleApi}
          onSettingsClick={handleSettings}
          onHelpCenterClick={handleHelpCenter}
          onAddEmail={handleAddEmail}
          languageOptions={languageOptions}
          currentLanguageCode={selectedLocale}
          onLanguageChange={handleLanguageChange}
          onLogout={handleLogout}
        />
      </div>
      <DialogDeposit isOpen={isDepositDialogOpen} setIsOpen={setIsDepositDialogOpen} />
      <DialogConvert
        isOpen={isConvertDialogOpen}
        setIsOpen={setIsConvertDialogOpen}
        onOpenDeposit={handleDeposit}
      />
      <DialogLogOut isOpen={isLogOutDialogOpen} setIsOpen={setIsLogOutDialogOpen} />
      <DialogAddEmail
        key={addEmailKey}
        isOpen={isAddEmailDialogOpen}
        setIsOpen={setIsAddEmailDialogOpen}
      />
    </>
  );
}
